import { useEffect, useState } from 'react';
import type { GraphNodeRecord } from '@reef/schema';
import { api } from '../api.ts';

type Related = {
  edge: string;
  direction: 'out' | 'in';
  node: GraphNodeRecord | { id: string; unresolved: true };
  weight: number;
};

/**
 * The context graph, for a person.
 *
 * The graph exists so that an agent can ask one question instead of twenty, and
 * this view exists so that a person can check the answer. That is its whole
 * job: when somebody says "the assistant told me payments owns this and that is
 * wrong", the fix is a look at the node and its edges, and without a view the
 * only way to look is a query somebody has to be taught to write.
 *
 * Search, then one node with everything around it. Two directions shown
 * together — what it points at and what points at it — because "what does this
 * depend on" and "what breaks if I change it" are the same edges read
 * backwards, and a view that made you switch would be answering half a
 * question.
 */
export function ContextGraphView() {
  const [counts, setCounts] = useState<{
    nodes: number;
    edges: number;
    byKind: Array<{ kind: string; count: number }>;
    bySource: Array<{ source: string; count: number }>;
  } | null>(null);
  const [query, setQuery] = useState('');
  const [kind, setKind] = useState('');
  const [results, setResults] = useState<GraphNodeRecord[]>([]);
  const [note, setNote] = useState<string | null>(null);
  const [selected, setSelected] = useState<GraphNodeRecord | null>(null);
  const [related, setRelated] = useState<Related[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api
      .contextGraph()
      .then((result) => setCounts(result.graph))
      .catch((caught) => setError((caught as Error).message));
  }, []);

  const search = async () => {
    setError(null);
    try {
      const answer = await api.graphSearch(query, kind || undefined);
      setResults(answer.nodes ?? []);
      setNote(answer.note ?? null);
    } catch (caught) {
      setError((caught as Error).message);
    }
  };

  const open = async (node: GraphNodeRecord) => {
    setSelected(node);
    setRelated([]);
    try {
      const answer = await api.graphNode(node.id);
      setRelated((answer.related ?? []) as Related[]);
      setNote(answer.note ?? null);
    } catch (caught) {
      setError((caught as Error).message);
    }
  };

  return (
    <div className="studio">
      <header className="studio-head">
        <span className="studio-crumb">
          <span className="studio-crumb-root">Context graph</span>
          {selected && <span className="studio-crumb-sep">/</span>}
          {selected && <span>{selected.title}</span>}
        </span>
        <span className="spacer" />
        {counts && (
          <span className="chip">
            {counts.nodes} nodes · {counts.edges} edges
          </span>
        )}
      </header>

      <div className="studio-column wide">
        {error && <div className="banner danger">{error}</div>}

        <section className="studio-card">
          <div className="studio-card-head">
            <h2>Find something</h2>
          </div>
          <div className="studio-card-body">
            <form
              onSubmit={(event) => {
                event.preventDefault();
                void search();
              }}
              style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}
            >
              <input
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="a service, a repository, an incident…"
                aria-label="Search the context graph"
                style={{ flex: '1 1 18rem' }}
              />
              <select value={kind} onChange={(event) => setKind(event.target.value)} aria-label="Kind">
                <option value="">any kind</option>
                {(counts?.byKind ?? []).map((entry) => (
                  <option key={entry.kind} value={entry.kind}>
                    {entry.kind} ({entry.count})
                  </option>
                ))}
              </select>
              <button type="submit">Search</button>
            </form>

            {note && <p className="hint">{note}</p>}

            {results.length > 0 && (
              <table>
                <thead>
                  <tr>
                    <th>Title</th>
                    <th>Kind</th>
                    <th>Owner</th>
                    <th>Source</th>
                  </tr>
                </thead>
                <tbody>
                  {results.map((node) => (
                    <tr key={node.id} onClick={() => void open(node)} style={{ cursor: 'pointer' }}>
                      <td>
                        {node.title}
                        {node.summary && <div className="tile-note">{node.summary}</div>}
                      </td>
                      <td>{node.kind}</td>
                      <td>{node.owner ?? '—'}</td>
                      <td>{node.source}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}

            {counts?.nodes === 0 && (
              <p className="hint">
                The graph is empty. The code half fills on the next push to an indexed repository;
                the workflow half fills on the next sweep, within a minute.
              </p>
            )}
          </div>
        </section>

        {selected && (
          <section className="studio-card">
            <div className="studio-card-head">
              <h2>{selected.title}</h2>
              <span className="chip">{selected.id}</span>
            </div>
            <div className="studio-card-body">
              {selected.summary && <p className="hint" style={{ marginTop: 0 }}>{selected.summary}</p>}
              {selected.url && (
                <p className="hint">
                  <a href={selected.url} target="_blank" rel="noreferrer">
                    {selected.url}
                  </a>
                </p>
              )}

              {related.length === 0 ? (
                <p className="hint">Nothing points at this node and it points at nothing.</p>
              ) : (
                <table>
                  <thead>
                    <tr>
                      <th>Direction</th>
                      <th>Edge</th>
                      <th>Node</th>
                    </tr>
                  </thead>
                  <tbody>
                    {related.map((entry, index) => (
                      <tr key={`${entry.edge}-${index}`}>
                        <td>{entry.direction === 'out' ? '→' : '←'}</td>
                        <td>{entry.edge}</td>
                        <td>
                          {'unresolved' in entry.node ? (
                            /*
                              An edge to something nobody has ingested. Shown
                              rather than hidden: "this points at a service Reef
                              has never indexed" is a fact, and omitting it makes
                              the graph look complete when it is not.
                            */
                            <span className="tile-note">{entry.node.id} — not ingested</span>
                          ) : (
                            <button
                              type="button"
                              className="linkish"
                              onClick={() => void open(entry.node as GraphNodeRecord)}
                            >
                              {(entry.node as GraphNodeRecord).title}
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </section>
        )}
      </div>
    </div>
  );
}
